import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supportMailto } from "@/lib/support";

interface Props {
  /** Show the ethics statement above contact + nav (used on the marketing landing). */
  showEthics?: boolean;
  /** Show the "Contact us at hello@relark.dk" callout block. */
  showContact?: boolean;
  /** Show the Features link in the legal nav. Defaults to true. */
  showFeaturesLink?: boolean;
  /** Show the Enterprise / "For institutions" link. Defaults to true. */
  showEnterpriseLink?: boolean;
  /** Show the Sign in link. Defaults to true. */
  showSignInLink?: boolean;
  /** Show the "Home" link first in the nav (used on sub-pages like Features). */
  showHomeLink?: boolean;
  className?: string;
  /**
   * @deprecated Brand prop is no longer used — Relark is the only brand.
   * Kept as no-op for callsite backwards compatibility.
   */
  brand?: "teendecode" | "relark";
}

/**
 * Shared footer for all public marketing & auth surfaces.
 * Renders: [optional ethics] [optional contact callout]
 *          [© line] [legal nav: Home? · Features · Enterprise · Privacy · Terms · Sign in]
 */
export function PublicFooter({
  showEthics = false,
  showContact = false,
  showFeaturesLink = true,
  showEnterpriseLink = true,
  showSignInLink = true,
  showHomeLink = false,
  className = "",
}: Props) {
  const { t } = useTranslation("ui");
  const year = new Date().getFullYear();
  const brandName = "Relark";
  const contactEmail = "hello@relark.dk";
  const contactHref = supportMailto("Relark");
  const helpHref = supportMailto("Relark support");
  const ethicsText = t("relark.footer.ethics", {
    defaultValue:
      "EU AI Act-kompatibel · Ingen salg af data · Designet til at styrke relationen, ikke kontrollere medarbejderen",
  });

  return (
    <footer className={`border-t py-12 ${className}`}>
      <div className="mx-auto max-w-6xl px-5">
        {showEthics && (
          <p className="text-center text-xs text-muted-foreground mb-6">
            {ethicsText}
          </p>
        )}
        {showContact && (
          <div className="mx-auto mb-6 max-w-sm rounded-lg bg-muted/50 px-5 py-3 text-center">
            <p className="text-sm text-muted-foreground">
              {t("landing.footer.contactLine")}{" "}
              <a
                href={contactHref}
                className="text-primary font-medium hover:underline"
              >
                {contactEmail}
              </a>
            </p>
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-10 text-sm">
          {/* Product */}
          <div>
            <p className="font-semibold mb-3">
              {t("relark.footer.col.product", { defaultValue: "Produkt" })}
            </p>
            <ul className="space-y-2 text-muted-foreground">
              <li>
                <Link to="/features" className="hover:text-foreground transition-colors">
                  {t("relark.footer.nav.features", { defaultValue: "Sådan virker det" })}
                </Link>
              </li>
              <li>
                <Link to="/science" className="hover:text-foreground transition-colors">
                  {t("relark.footer.nav.science", { defaultValue: "Videnskaben bag" })}
                </Link>
              </li>
              <li>
                <Link to="/pricing" className="hover:text-foreground transition-colors">
                  {t("relark.footer.nav.pricing", { defaultValue: "Priser" })}
                </Link>
              </li>
              {showEnterpriseLink && (
                <li>
                  <Link to="/enterprise" className="hover:text-foreground transition-colors">
                    {t("landing.footer.institutions", { defaultValue: "Enterprise" })}
                  </Link>
                </li>
              )}
            </ul>
          </div>

          {/* Audience */}
          <div>
            <p className="font-semibold mb-3">
              {t("relark.footer.col.audience", { defaultValue: "Til hvem" })}
            </p>
            <ul className="space-y-2 text-muted-foreground">
              <li>
                <Link to="/for-hr" className="hover:text-foreground transition-colors">
                  {t("nav.publicHeader.audience.hr.title", { defaultValue: "HR-direktør" })}
                </Link>
              </li>
              <li>
                <Link to="/for-kommune" className="hover:text-foreground transition-colors">
                  {t("nav.publicHeader.audience.municipality.title", { defaultValue: "Kommune" })}
                </Link>
              </li>
              <li>
                <Link to="/for-it" className="hover:text-foreground transition-colors">
                  {t("nav.publicHeader.audience.it.title", { defaultValue: "IT & sikkerhed" })}
                </Link>
              </li>
              <li>
                <Link to="/enterprise" className="hover:text-foreground transition-colors">
                  {t("nav.publicHeader.audience.enterprise.title", { defaultValue: "Enterprise" })}
                </Link>
              </li>
            </ul>
          </div>

          {/* Trust */}
          <div>
            <p className="font-semibold mb-3">
              {t("relark.footer.col.trust", { defaultValue: "Sikkerhed & jura" })}
            </p>
            <ul className="space-y-2 text-muted-foreground">
              <li>
                <Link to="/trust" className="hover:text-foreground transition-colors">
                  {t("relark.footer.nav.trust", { defaultValue: "Sikkerhed & jura" })}
                </Link>
              </li>
              <li>
                <a
                  href="/Relark_EU_AI_Act_Risk_Assessment.pdf"
                  target="_blank"
                  rel="noopener"
                  className="hover:text-foreground transition-colors"
                >
                  {t("relark.footer.nav.aiAct", { defaultValue: "EU AI Act" })}
                </a>
              </li>
              <li>
                <Link to="/privacy" className="hover:text-foreground transition-colors">
                  {t("nav.privacyPolicy")}
                </Link>
              </li>
              <li>
                <Link to="/terms" className="hover:text-foreground transition-colors">
                  {t("nav.termsOfService")}
                </Link>
              </li>
              <li>
                <Link to="/refund" className="hover:text-foreground transition-colors">
                  {t("nav.refundPolicy", { defaultValue: "Refusionspolitik" })}
                </Link>
              </li>
            </ul>
          </div>

          {/* Support */}
          <div>
            <p className="font-semibold mb-3">
              {t("relark.footer.col.support", { defaultValue: "Support" })}
            </p>
            <ul className="space-y-2 text-muted-foreground">
              <li>
                <a href={contactHref} className="hover:text-foreground transition-colors">
                  {t("relark.footer.col.contact", { defaultValue: "Kontakt" })}
                </a>
              </li>
              <li>
                <a href={helpHref} className="hover:text-foreground transition-colors">
                  {t("nav.help", { defaultValue: "Hjælp" })}
                </a>
              </li>
              {showHomeLink && (
                <li>
                  <Link to="/" className="hover:text-foreground transition-colors">
                    {t("nav.home", { defaultValue: "Home" })}
                  </Link>
                </li>
              )}
              {showSignInLink && (
                <li>
                  <Link to="/auth" className="hover:text-foreground transition-colors">
                    {t("auth.signIn")}
                  </Link>
                </li>
              )}
            </ul>
          </div>
        </div>

        <div className="border-t pt-6 flex flex-col items-center gap-2 sm:flex-row sm:justify-between">
          <p className="text-sm text-muted-foreground">
            © {year} {brandName}. {t("landing.footer.rights")}
          </p>
          <p className="text-xs text-muted-foreground">
            🇪🇺 {t("landing.footer.euHosted", { defaultValue: "Hosted in EU · Paris (AWS eu-west-3)" })}
          </p>
        </div>
      </div>
    </footer>
  );
}
